import type { JSX } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { readThemeChoice, saveThemeChoice, THEME_LABEL, type ThemeChoice } from '../lib/theme.js';
import { Avatar, Button, ImageUploadButton, Modal, Notice, TextAreaField, TextField } from '../components/ui.js';
import { IconPhone } from '../components/icons.js';

/**
 * Профиль и настройки.
 *
 * Здесь же живёт установка приложения на телефон: подсказка показывается только
 * тогда, когда браузер действительно готов его установить, — иначе это лишний
 * шум для тех, кто уже установил или у кого браузер не поддерживает установку.
 */

export function ProfilePage(): JSX.Element {
  const { user, logout, refresh } = useAuth();
  const queryClient = useQueryClient();

  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [username, setUsername] = useState(user?.username ?? '');
  const [bio, setBio] = useState(user?.bio ?? '');
  const [birthday, setBirthday] = useState(user?.birthday ?? '');
  const [saved, setSaved] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordDone, setPasswordDone] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);

  /*
   * Удаление аккаунта.
   *
   * После успеха страница перезагружается целиком, а не переключается
   * маршрутом: в памяти остаются кэши запросов с уже несуществующими данными,
   * и самый честный способ их выбросить — начать с чистого листа.
   */
  const deleteAccount = useMutation({
    mutationFn: () => api.auth.deleteAccount(deletePassword),
    onSuccess: () => {
      window.location.href = '/';
    },
    onError: (error) => {
      setDeleteError(error instanceof ApiError ? error.message : 'Не получилось удалить аккаунт');
    },
  });

  const saveProfile = useMutation({
    mutationFn: () =>
      api.users.updateProfile({
        displayName: displayName.trim(),
        ...(username.trim() ? { username: username.trim().toLowerCase() } : {}),
        bio: bio.trim(),
        birthday: birthday || null,
      }),
    onSuccess: async () => {
      await refresh();
      void queryClient.invalidateQueries();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    },
  });

  const changePassword = useMutation({
    mutationFn: () => api.auth.changePassword(currentPassword, newPassword),
    onSuccess: () => {
      setCurrentPassword('');
      setNewPassword('');
      setPasswordDone(true);
      setTimeout(() => setPasswordDone(false), 3000);
    },
  });

  if (!user) return <></>;

  return (
    <>
      <div className="page-header">
        <div className="row">
          <Avatar name={user.displayName} src={user.avatarUrl} size="lg" />
          <div>
            <h1>{user.displayName}</h1>
            {user.username && <p className="muted small">@{user.username}</p>}
            <div style={{ marginTop: 'var(--space-2)' }}>
              <ImageUploadButton
                size="sm"
                label={user.avatarUrl ? 'Сменить фото' : 'Добавить фото'}
                onUploaded={async (url) => {
                  await api.users.updateProfile({ avatarUrl: url });
                  await refresh();
                  void queryClient.invalidateQueries();
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="stack stack--lg">
        <section className="panel stack">
          <h2>О себе</h2>

          <TextField
            label="Имя"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            maxLength={60}
          />

          <TextField
            label="Имя пользователя"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            placeholder="masha"
            hint="По нему вас найдут друзья. Латиница, цифры и _."
          />

          <TextField
            label="День рождения"
            type="date"
            value={birthday}
            onChange={(event) => setBirthday(event.target.value)}
            hint="Друзья увидят, что праздник близко, и успеют подготовиться."
          />

          <TextAreaField
            label="Пара слов"
            value={bio}
            onChange={(event) => setBio(event.target.value)}
            maxLength={500}
            rows={2}
            placeholder="Что-нибудь о себе"
          />

          {saveProfile.error instanceof ApiError && (
            <Notice tone="danger">
              {saveProfile.error.message}
            </Notice>
          )}
          {saved && <Notice tone="success">Сохранили</Notice>}

          <Button variant="primary" loading={saveProfile.isPending} onClick={() => saveProfile.mutate()}>
            Сохранить
          </Button>
        </section>

        <ThemeSection />

        {/*
          Ссылка на себя. Чтобы подружиться, иначе нужно диктовать имя
          пользователя голосом и надеяться, что его наберут без опечатки, —
          а так ссылка просто отправляется в мессенджер.
        */}
        {user.username && <MyLinkSection username={user.username} />}

        <InstallSection />

        <section className="panel stack">
          <h2>Пароль</h2>

          <TextField
            label="Текущий пароль"
            type="password"
            value={currentPassword}
            onChange={(event) => setCurrentPassword(event.target.value)}
            autoComplete="current-password"
          />
          <TextField
            label="Новый пароль"
            type="password"
            value={newPassword}
            onChange={(event) => setNewPassword(event.target.value)}
            autoComplete="new-password"
            hint="Минимум 8 символов. После смены все другие устройства выйдут из аккаунта."
          />

          {changePassword.error instanceof ApiError && (
            <Notice tone="danger">
              {changePassword.error.message}
            </Notice>
          )}
          {passwordDone && <Notice tone="success">Пароль изменён</Notice>}

          <Button
            variant="ghost"
            loading={changePassword.isPending}
            disabled={!currentPassword || newPassword.length < 8}
            onClick={() => changePassword.mutate()}
          >
            Сменить пароль
          </Button>
        </section>

        <section className="panel stack">
          <h2>Аккаунт</h2>
          <p className="small muted">Вы вошли как {user.email}</p>
          <Button variant="ghost" onClick={() => void logout()}>
            Выйти
          </Button>

          <hr className="divider" />

          <div className="stack stack--sm">
            <span className="small strong">Удалить аккаунт</span>
            <span className="tiny muted">
              Уйдут списки, желания, фотографии и дружбы. Брони, которые вы ставили на чужие
              подарки, снимутся — вещи снова станут свободными, и владельцы, как и раньше,
              ничего не заметят. Восстановить будет нечем.
            </span>
            <Button
              variant="ghost"
              className="btn--danger"
              onClick={() => {
                setDeletePassword('');
                setDeleteError(null);
                setDeleteOpen(true);
              }}
            >
              Удалить аккаунт
            </Button>
          </div>
        </section>
      </div>

      <Modal open={deleteOpen} title="Удалить аккаунт" onClose={() => setDeleteOpen(false)}>
        <div className="stack">
          <Notice tone="danger">
            Это навсегда. Ни списки, ни фотографии восстановить не получится.
          </Notice>

          <TextField
            label="Введите пароль, чтобы подтвердить"
            type="password"
            value={deletePassword}
            onChange={(event) => setDeletePassword(event.target.value)}
            autoComplete="current-password"
            autoFocus
          />

          {deleteError && (
            <Notice tone="danger">
              {deleteError}
            </Notice>
          )}

          <div className="row">
            <Button variant="ghost" onClick={() => setDeleteOpen(false)} block>
              Оставить всё как есть
            </Button>
            <Button
              variant="ghost"
              className="btn--danger"
              loading={deleteAccount.isPending}
              disabled={!deletePassword}
              onClick={() => deleteAccount.mutate()}
              block
            >
              Удалить навсегда
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

/**
 * Выбор темы.
 *
 * По умолчанию — как в системе, и для большинства этого достаточно. Но
 * системная настройка одна на всё устройство, а привычки разные: кто-то держит
 * систему тёмной, а списки желаний предпочитает светлыми.
 */
function ThemeSection(): JSX.Element {
  const [choice, setChoice] = useState<ThemeChoice>(() => readThemeChoice());

  return (
    <section className="panel stack">
      <h2>Оформление</h2>
      <div className="chip-row">
        {(['system', 'light', 'dark'] as const).map((value) => (
          <button
            key={value}
            type="button"
            className={`chip ${choice === value ? 'chip--active' : ''}`.trim()}
            aria-pressed={choice === value}
            onClick={() => {
              setChoice(value);
              saveThemeChoice(value);
            }}
          >
            {value === 'light' ? '☀️ ' : value === 'dark' ? '🌙 ' : ''}
            {THEME_LABEL[value]}
          </button>
        ))}
      </div>
      <p className="tiny faint">
        Выбор запоминается на этом устройстве. На телефоне и за компьютером
        могут быть удобны разные темы.
      </p>
    </section>
  );
}

/** Личная ссылка: её отправляют друзьям, чтобы те могли добавиться. */
function MyLinkSection({ username }: { username: string }): JSX.Element {
  const [copied, setCopied] = useState(false);
  const link = `${window.location.origin}/u/${username}`;

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      // Буфер обмена недоступен (старый браузер, доступ без https) —
      // показываем ссылку, чтобы её выделили руками.
      window.prompt('Скопируйте ссылку:', link);
    }
  }

  return (
    <section className="panel stack">
      <h2>Ваша ссылка</h2>
      <p className="small muted">
        Отправьте её друзьям — они смогут добавиться к вам и увидеть списки.
        Диктовать имя пользователя не придётся.
      </p>
      <div className="row row--wrap" style={{ gap: 'var(--space-3)' }}>
        <code className="small truncate" style={{ color: 'var(--accent)' }}>
          {link}
        </code>
        <Button variant="ghost" size="sm" onClick={() => void copy()}>
          {copied ? '✓ Скопировано' : 'Скопировать'}
        </Button>
      </div>
    </section>
  );
}

/**
 * Установка на телефон.
 *
 * Android и десктопный Chrome умеют показывать системное окно установки — для них
 * ловим событие beforeinstallprompt и предлагаем кнопку. Safari на iPhone такого
 * события не даёт, поэтому там показываем короткую инструкцию: «Поделиться» →
 * «На экран Домой». Без этой подсказки пользователи iPhone просто не догадаются.
 */
function InstallSection(): JSX.Element | null {
  const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const onBeforeInstall = (event: Event): void => {
      // Браузер по умолчанию сам решает, когда показать окно установки.
      // Перехватываем событие, чтобы предложить установку в понятном месте.
      event.preventDefault();
      setPrompt(event as BeforeInstallPromptEvent);
    };
    const onInstalled = (): void => setInstalled(true);

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const isStandalone =
    typeof window !== 'undefined' &&
    (window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as unknown as { standalone?: boolean }).standalone === true);

  if (isStandalone || installed) return null;

  const isIos = typeof navigator !== 'undefined' && /iphone|ipad|ipod/i.test(navigator.userAgent);

  return (
    <section className="panel stack">
      <h2>Приложение на телефоне</h2>
      <p className="small muted">
        Загадарь ставится на домашний экран и открывается как обычное приложение — без адресной строки, со своей иконкой.
      </p>

      {prompt ? (
        <Button
          variant="primary"
          onClick={() => {
            void prompt.prompt();
            // Событие одноразовое: повторно показать то же окно нельзя.
            setPrompt(null);
          }}
        >
          Установить приложение
        </Button>
      ) : isIos ? (
        <Notice tone="info" icon={<IconPhone size={19} />}>
          <span className="small">
            В Safari нажмите «Поделиться» <strong>⎋</strong>, затем «На экран „Домой“».
          </span>
        </Notice>
      ) : (
        <Notice tone="info" icon={<IconPhone size={19} />}>
          <span className="small">
            В меню браузера выберите «Установить приложение» или «Добавить на главный экран».
          </span>
        </Notice>
      )}
    </section>
  );
}
